import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    Upload,
    message,
} from 'antd';
import type { TableColumnsType, UploadProps } from 'antd';
import {
    ArrowLeftOutlined,
    ClearOutlined,
    DownloadOutlined,
    LockOutlined,
    ReloadOutlined,
    SaveOutlined,
    SendOutlined,
    UnlockOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import {
    exportBracketDraftXLSX,
    generateBracketDraft,
    getBracketDraft,
    importBracketDraftXLSX,
    publishBracketDraft,
    resetBracketDraft,
    updateBracketDraft,
} from '../../apiBridge';
import {
    ActivityCategoryInfo,
    ActivityOutletContext,
    BracketAdjustmentMode,
    BracketDraftImportAssignment,
    BracketDraftImportPreview,
    BracketDraftImportRowError,
    BracketDraftInfo,
    BracketDraftSlot,
    MatchParticipant,
} from '../../interface';
import {
    bracketRoundName,
    bracketSessionLabel,
    formatBracketParticipantLabel,
    formatPlanClock,
    qualifierWinnerLabel,
    swapBracketParticipants,
} from './bracketBuilderUtils';
import '../../assets/BracketBuilder.css';

const { Text, Title, Paragraph } = Typography;

const adjustmentModeLabels: Record<BracketAdjustmentMode, string> = {
    perfect: 'Standard draw',
    bye: 'BYE',
    extra: 'Extra matches',
};

const statusLabels: Record<BracketDraftInfo['status'], string> = {
    not_created: 'Not created',
    draft: 'Draft',
    published: 'Published',
};

const statusColors: Record<BracketDraftInfo['status'], string> = {
    not_created: 'default',
    draft: 'processing',
    published: 'success',
};

function addMinutesToClock(value: string, minutes: number): string | null {
    const clock = formatPlanClock(value);
    if (!clock) {
        return null;
    }
    const [hours, mins] = clock.split(':').map(Number);
    const total = hours * 60 + mins + minutes;
    const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
    return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

function matchDurationMinutes(gameRounds?: number): number {
    if (gameRounds === 1) {
        return 6;
    }
    if (gameRounds === 3) {
        return 10;
    }
    return 15;
}

function formatScheduleParts(parts: {
    groupKey?: string;
    court?: number;
    date?: string;
    start?: string | null;
    end?: string | null;
}): string | null {
    const labels: string[] = [];
    if (parts.groupKey) {
        labels.push(parts.groupKey);
    }
    if (parts.court) {
        labels.push(`Court ${parts.court}`);
    }
    const timeRange = parts.start && parts.end ? `${parts.start}–${parts.end}` : parts.start;
    const when = [parts.date, timeRange].filter(Boolean).join(' ');
    if (when) {
        labels.push(when);
    }
    return labels.length ? labels.join(' · ') : null;
}

function draftMatchScheduleLabel(
    matchSlots: BracketDraftSlot[],
    phaseSlots: BracketDraftSlot[],
    category?: ActivityCategoryInfo | null,
): string | null {
    const slot = matchSlots.find((item) => item.group_key || item.plan_date || item.court);
    if (slot) {
        const fromApi = formatScheduleParts({
            groupKey: slot.group_key,
            court: slot.court,
            date: slot.plan_date,
            start: formatPlanClock(slot.plan_start_at),
            end: formatPlanClock(slot.plan_end_at),
        });
        if (fromApi) {
            return fromApi;
        }
    }

    const sample = matchSlots[0];
    if (!sample || matchSlots.some((item) => item.kind === 'bye')) {
        return null;
    }
    const round =
        category?.options?.rounds?.[sample.round] ||
        category?.options?.rounds?.[Number(sample.round)];
    if (!round) {
        return null;
    }
    const matchesByIndex = new Map<number, BracketDraftSlot[]>();
    phaseSlots
        .filter((item) => item.round === sample.round)
        .forEach((item) => {
            const current = matchesByIndex.get(item.match_index) || [];
            current.push(item);
            matchesByIndex.set(item.match_index, current);
        });
    const playableIndexes = Array.from(matchesByIndex.keys())
        .sort((left, right) => left - right)
        .filter((index) => !(matchesByIndex.get(index) || []).some((item) => item.kind === 'bye'));
    const playablePosition = playableIndexes.indexOf(sample.match_index);
    if (playablePosition < 0) {
        return null;
    }
    const courts = Math.max(1, round.courts || 1);
    const group = (playablePosition % courts) + 1;
    const offset = Math.floor(playablePosition / courts);
    const duration = matchDurationMinutes(round.game_rounds);
    const scheduled = (round.schedule || []).find((item) => Number(item.type) === group);
    const start = scheduled ? formatPlanClock(scheduled.start_at) : null;
    const end = start ? addMinutesToClock(start, duration) : null;
    return formatScheduleParts({
        groupKey: `G${group}`,
        court: scheduled?.court,
        date: scheduled?.date,
        start,
        end,
    });
}

async function readResponse<T>(response?: Response): Promise<T> {
    if (!response) {
        throw new Error('No response from match service');
    }

    let data: any = null;
    try {
        data = await response.json();
    } catch {
        // A non-JSON error body is handled by the generic status message below.
    }

    if (!response.ok || data?.error) {
        throw new Error(data?.error || `Request failed (${response.status})`);
    }
    return data as T;
}

// readJsonResponse keeps the HTTP status so the import flow can distinguish
// stale revisions (409) from row-level validation failures (422).
async function readJsonResponse(response?: Response): Promise<{ status: number; data: any }> {
    if (!response) {
        throw new Error('No response from match service');
    }

    let data: any = null;
    try {
        data = await response.json();
    } catch {
        // A non-JSON error body is reported through the status code by the caller.
    }
    return { status: response.status, data };
}

const importPreviewColumns: TableColumnsType<BracketDraftImportAssignment> = [
    {
        title: 'Slot',
        dataIndex: 'slot_key',
        key: 'slot_key',
        render: (value: string) => <Text code>{value}</Text>,
    },
    {
        title: 'Participant',
        dataIndex: 'participant_name',
        key: 'participant_name',
    },
    {
        title: 'License Number',
        dataIndex: 'license_number',
        key: 'license_number',
        render: (value: string) => value || '—',
    },
    {
        title: 'Seed lock',
        dataIndex: 'is_seed_locked',
        key: 'is_seed_locked',
        render: (locked: boolean) => (locked ? <Tag color="blue">Locked</Tag> : '—'),
    },
];

const importRowErrorColumns: TableColumnsType<BracketDraftImportRowError> = [
    {
        title: 'Row',
        dataIndex: 'row',
        key: 'row',
        render: (row: number) => (row > 0 ? row : 'Workbook'),
    },
    {
        title: 'Column',
        dataIndex: 'column',
        key: 'column',
        render: (column: string) => column || '—',
    },
    {
        title: 'Error',
        dataIndex: 'message',
        key: 'message',
    },
];

const BracketBuilder: React.FC = () => {
    const { id, cid } = useParams();
    const [searchParams] = useSearchParams();
    const focusSlotKey = searchParams.get('slot');
    const navigate = useNavigate();
    const {
        activity,
        categories,
        reloadData,
        setHasUnsavedChanges,
    } = useOutletContext() as ActivityOutletContext;
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [draft, setDraft] = useState<BracketDraftInfo | null>(null);
    const [workingSlots, setWorkingSlots] = useState<BracketDraftSlot[]>([]);
    const [dirty, setDirty] = useState(false);
    const [importPreview, setImportPreview] = useState<{
        file: File;
        result: BracketDraftImportPreview;
    } | null>(null);
    const [importPreviewLoading, setImportPreviewLoading] = useState(false);
    const [importConfirming, setImportConfirming] = useState(false);
    const [randomizeModalOpen, setRandomizeModalOpen] = useState(false);

    const category = categories?.find((item) => String(item.id) === String(cid));

    useEffect(() => {
        if (loading || !focusSlotKey) {
            return undefined;
        }
        const frame = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const node = Array.from(document.querySelectorAll('[data-slot-key]')).find(
                    (item) => item.getAttribute('data-slot-key') === focusSlotKey,
                );
                if (node instanceof HTMLElement) {
                    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [loading, focusSlotKey, workingSlots]);

    const acceptServerDraft = (nextDraft: BracketDraftInfo) => {
        setDraft(nextDraft);
        setWorkingSlots(nextDraft.slots);
        setDirty(false);
        setHasUnsavedChanges?.(false);
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError('');

        if (!id || !cid) {
            setLoadError('Activity or category ID is missing');
            setLoading(false);
            return () => undefined;
        }

        getBracketDraft(id, cid)
            .then((response) => readResponse<BracketDraftInfo>(response))
            .then((data) => {
                if (active) {
                    acceptServerDraft(data);
                }
            })
            .catch((error: Error) => {
                if (active) {
                    setLoadError(error.message);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
            setHasUnsavedChanges?.(false);
        };
        // The route IDs define which server draft should be loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, cid]);

    const participantByID = useMemo(() => {
        const result = new Map<string, MatchParticipant>();
        draft?.participants.forEach((participant) => result.set(participant.id, participant));
        return result;
    }, [draft?.participants]);

    const participantSlots = useMemo(
        () => workingSlots.filter((slot) => slot.kind === 'participant'),
        [workingSlots],
    );
    const lockedCount = participantSlots.filter((slot) => slot.is_seed_locked).length;
    const assignedCount = participantSlots.filter((slot) => slot.participant_id).length;

    const markDirty = (nextSlots: BracketDraftSlot[]) => {
        setWorkingSlots(nextSlots);
        setDirty(true);
        setHasUnsavedChanges?.(true);
    };

    const participantName = (participant?: MatchParticipant) => {
        if (!participant) {
            return 'Unassigned participant';
        }
        return (
            participant.display_name ||
            participant.team_name ||
            [participant.first_name, participant.last_name].filter(Boolean).join(' ') ||
            participant.id
        );
    };

    const participantSelectLabel = (participant: MatchParticipant) => {
        const licenseNumber = participant.license_number?.trim();

        return (
            <span className="bracket-builder-participant-label">
                <span>{participantName(participant)}</span>
                {licenseNumber && (
                    <span className="bracket-builder-participant-license">
                        {licenseNumber}
                    </span>
                )}
            </span>
        );
    };

    const returnToCategories = () => {
        const goBack = () => {
            setHasUnsavedChanges?.(false);
            if (draft?.status === 'published') {
                navigate(`/activity/${activity.id}/bracket?category=${cid}`);
                return;
            }
            navigate(`/activity/${activity.id}/category`);
        };
        if (!dirty) {
            goBack();
            return;
        }
        Modal.confirm({
            title: 'Discard unsaved changes?',
            content: 'Your unsaved participant positions and seed locks will be lost.',
            okText: 'Discard',
            okButtonProps: { danger: true },
            onOk: goBack,
        });
    };

    const runDraftMutation = async (
        operation: () => Promise<Response | undefined>,
        successMessage: string,
    ) => {
        setSubmitting(true);
        try {
            const nextDraft = await readResponse<BracketDraftInfo>(await operation());
            acceptServerDraft(nextDraft);
            messageApi.success(successMessage);
            reloadData('');
        } catch (error: any) {
            messageApi.error(error.message || 'Unable to update bracket draft');
        } finally {
            setSubmitting(false);
        }
    };

    const createDraft = () => {
        if (!id || !cid || !draft) {
            return;
        }
        runDraftMutation(
            () => generateBracketDraft(id, cid, draft.revision),
            'Draft created and participants randomly placed',
        );
    };

    const currentAssignments = () =>
        participantSlots.map((slot) => ({
            slot_key: slot.key,
            participant_id: slot.participant_id || '',
            is_seed_locked: slot.is_seed_locked && Boolean(slot.participant_id),
        }));

    const regenerate = (mode: 'unlocked' | 'unassigned') => {
        if (!id || !cid || !draft) {
            return;
        }
        setRandomizeModalOpen(false);
        runDraftMutation(
            async () => {
                // Randomize always reflects what is on screen: unsaved edits
                // are saved first so the server draft matches the local one.
                let revision = draft.revision;
                if (dirty) {
                    const saved = await readResponse<BracketDraftInfo>(
                        await updateBracketDraft(id, cid, revision, currentAssignments()),
                    );
                    revision = saved.revision;
                }
                return generateBracketDraft(id, cid, revision, mode);
            },
            mode === 'unassigned'
                ? 'Unassigned positions randomly filled'
                : 'Unlocked positions randomized',
        );
    };

    const swapSlots = (sourceKey: string, targetKey: string) => {
        const nextSlots = swapBracketParticipants(workingSlots, sourceKey, targetKey);
        if (nextSlots === workingSlots) {
            if (sourceKey !== targetKey) {
                messageApi.warning('Locked positions cannot be moved');
            }
            return;
        }
        markDirty(nextSlots);
    };

    const chooseParticipant = (targetKey: string, participantID?: string) => {
        const target = workingSlots.find((slot) => slot.key === targetKey);
        if (!target || target.is_seed_locked) {
            return;
        }
        if (!participantID) {
            markDirty(
                workingSlots.map((slot) =>
                    slot.key === targetKey ? { ...slot, participant_id: undefined } : slot,
                ),
            );
            return;
        }
        const source = workingSlots.find((slot) => slot.participant_id === participantID);
        if (source) {
            swapSlots(source.key, targetKey);
            return;
        }
        // The participant is not placed in any slot yet: assign them to the
        // target, evicting the current occupant back to the unplaced pool.
        markDirty(
            workingSlots.map((slot) =>
                slot.key === targetKey ? { ...slot, participant_id: participantID } : slot,
            ),
        );
    };

    const updateLocks = (slotKeys: Set<string>, locked: boolean) => {
        const nextSlots = workingSlots.map((slot) =>
            slot.kind === 'participant' && slotKeys.has(slot.key)
                ? { ...slot, is_seed_locked: locked }
                : slot,
        );
        markDirty(nextSlots);
    };

    const toggleLock = (slotKey: string) => {
        const slot = workingSlots.find((item) => item.key === slotKey);
        if (!slot) {
            return;
        }
        updateLocks(new Set([slotKey]), !slot.is_seed_locked);
    };

    const clearAllPositions = () => {
        markDirty(
            workingSlots.map((slot) =>
                slot.kind === 'participant'
                    ? { ...slot, participant_id: undefined, is_seed_locked: false }
                    : slot,
            ),
        );
    };

    const saveDraft = async () => {
        if (!id || !cid || !draft) {
            return;
        }
        await runDraftMutation(
            () => updateBracketDraft(id, cid, draft.revision, currentAssignments()),
            'Draft saved',
        );
    };

    const exportDraftWorkbook = async () => {
        if (!id || !cid || !draft || dirty) {
            return;
        }
        setExporting(true);
        try {
            const response = await exportBracketDraftXLSX(id, cid);
            if (!response.ok) {
                await readResponse(response);
                return;
            }
            const data = await response.blob();
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            const safeCategoryName = (category?.name || `category-${cid}`)
                .replace(/[\\/:*?"<>|]+/g, '-')
                .trim();
            link.href = url;
            link.download = `bracket-draft-${safeCategoryName || cid}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            messageApi.success('Bracket draft XLSX exported');
        } catch (error: any) {
            messageApi.error(error.message || 'Unable to export bracket draft XLSX');
        } finally {
            setExporting(false);
        }
    };

    const staleRevisionMessage =
        'This bracket draft changed on the server. Reload the page before importing again.';

    const showImportError = (title: string, content?: string) => {
        Modal.error({
            title,
            width: 640,
            content: content || undefined,
        });
    };

    const showImportRowErrors = (rowErrors: BracketDraftImportRowError[], title: string) => {
        Modal.error({
            title,
            width: 640,
            content: (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                        {rowErrors.map((error, index) => (
                            <li key={`${error.row}-${error.column}-${index}`}>
                                {error.row > 0 ? `Row ${error.row}` : 'Workbook'}
                                {error.column ? `, ${error.column}` : ''}: {error.message}
                            </li>
                        ))}
                    </ul>
                </div>
            ),
        });
    };

    const previewImportDraftWorkbook = async (file: File) => {
        if (!id || !cid || !draft) {
            return;
        }
        setImportPreviewLoading(true);
        try {
            const { status, data } = await readJsonResponse(
                await importBracketDraftXLSX(id, cid, draft.revision, file, {
                    dryRun: true,
                    mode: 'partial',
                }),
            );
            if (status === 409) {
                showImportError('Unable to preview bracket draft XLSX', staleRevisionMessage);
                return;
            }
            if (status === 422) {
                showImportRowErrors(
                    data?.row_errors || [],
                    data?.error || 'The XLSX workbook contains invalid rows',
                );
                return;
            }
            if (data?.error || status >= 400) {
                showImportError(
                    'Unable to preview bracket draft XLSX',
                    data?.error || `Request failed (${status})`,
                );
                return;
            }
            setImportPreview({
                file,
                result: {
                    assignments: data?.assignments || [],
                    row_errors: data?.row_errors || [],
                },
            });
        } catch (error: any) {
            showImportError(
                'Unable to preview bracket draft XLSX',
                error.message || 'Unexpected error while reading the XLSX file',
            );
        } finally {
            setImportPreviewLoading(false);
        }
    };

    const confirmImportDraftWorkbook = async () => {
        if (!id || !cid || !draft || !importPreview) {
            return;
        }
        setImportConfirming(true);
        try {
            const { status, data } = await readJsonResponse(
                await importBracketDraftXLSX(id, cid, draft.revision, importPreview.file, {
                    mode: 'partial',
                }),
            );
            if (status === 409) {
                showImportError('Unable to import bracket draft XLSX', staleRevisionMessage);
                return;
            }
            if (status === 422) {
                showImportRowErrors(
                    data?.row_errors || [],
                    data?.error || 'The XLSX workbook contains invalid rows',
                );
                return;
            }
            if (data?.error || status >= 400) {
                showImportError(
                    'Unable to import bracket draft XLSX',
                    data?.error || `Request failed (${status})`,
                );
                return;
            }
            acceptServerDraft(data as BracketDraftInfo);
            messageApi.success('Bracket draft XLSX imported and saved');
            reloadData('');
            setImportPreview(null);
        } catch (error: any) {
            showImportError(
                'Unable to import bracket draft XLSX',
                error.message || 'Unexpected error while saving the XLSX file',
            );
        } finally {
            setImportConfirming(false);
        }
    };

    const beforeImportDraftWorkbook: UploadProps['beforeUpload'] = (file) => {
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            messageApi.error('Please select an .xlsx file');
            return Upload.LIST_IGNORE;
        }
        previewImportDraftWorkbook(file as File);
        return false;
    };

    const publishDraft = () => {
        if (!id || !cid || !draft || dirty) {
            return;
        }
        const doPublish = async (revision: number) => {
            setSubmitting(true);
            try {
                await readResponse(
                    await publishBracketDraft(id, cid, revision),
                );
                setHasUnsavedChanges?.(false);
                messageApi.success('Bracket published');
                reloadData('category');
            } catch (error: any) {
                messageApi.error(error.message || 'Unable to publish bracket');
            } finally {
                setSubmitting(false);
            }
        };
        const unassignedCount = participantSlots.length - assignedCount;
        if (unassignedCount > 0) {
            // Publishing requires every position to be filled, so offer to
            // randomize the unassigned ones first.
            Modal.confirm({
                title: 'Randomize unassigned positions?',
                content: `${unassignedCount} participant position(s) are still unassigned and the bracket cannot be published until every position is filled. Randomly fill them and publish?`,
                okText: 'Randomize and publish',
                cancelText: 'Cancel',
                onOk: async () => {
                    setSubmitting(true);
                    try {
                        const randomized = await readResponse<BracketDraftInfo>(
                            await generateBracketDraft(id, cid, draft.revision, 'unassigned'),
                        );
                        setSubmitting(false);
                        await doPublish(randomized.revision);
                    } catch (error: any) {
                        messageApi.error(error.message || 'Unable to randomize unassigned positions');
                        setSubmitting(false);
                    }
                },
            });
            return;
        }
        Modal.confirm({
            title: 'Publish bracket?',
            content:
                'This creates the competition matches from the current draft. You can reset it before the first match starts.',
            okText: 'Publish',
            onOk: () => doPublish(draft.revision),
        });
    };

    const resetToDraft = () => {
        if (!id || !cid || !draft) {
            return;
        }
        Modal.confirm({
            title: 'Reset published bracket to draft?',
            content:
                'Existing matches and schedules for this category will be removed. Participant positions and seed locks will be preserved.',
            okText: 'Reset to draft',
            okButtonProps: { danger: true },
            onOk: () =>
                runDraftMutation(
                    () => resetBracketDraft(id, cid, draft.revision),
                    'Bracket reset to draft',
                ),
        });
    };

    const renderSlot = (slot: BracketDraftSlot) => {
        if (slot.kind === 'bye') {
            return (
                <div className="bracket-builder-slot bracket-builder-placeholder">
                    <Tag color="gold">BYE</Tag>
                    <Text type="secondary">Automatically advances opponent</Text>
                </div>
            );
        }
        if (slot.kind === 'qualifier') {
            const preliminaryRound = workingSlots.find((item) => item.phase === 'preliminary')?.round ?? 0;
            return (
                <div className="bracket-builder-slot bracket-builder-placeholder">
                    <Tag color="purple">Qualifier</Tag>
                    <Text type="secondary">
                        {qualifierWinnerLabel(
                            bracketRoundName(draft?.round_names, preliminaryRound),
                            slot.source_match_index ?? 0,
                        )}
                    </Text>
                </div>
            );
        }

        const readOnly = draft?.status !== 'draft' || draft.competition_locked;
        const participant = participantByID.get(slot.participant_id || '');
        const options = draft?.participants.map((candidate) => {
            const candidateSlot = workingSlots.find(
                (item) => item.participant_id === candidate.id,
            );
            return {
                value: candidate.id,
                label: participantSelectLabel(candidate),
                disabled:
                    candidate.id !== slot.participant_id &&
                    Boolean(candidateSlot?.is_seed_locked),
            };
        });

        return (
            <div
                className={[
                    'bracket-builder-slot',
                    slot.is_seed_locked ? 'bracket-builder-slot-locked' : '',
                    slot.key === focusSlotKey ? 'bracket-builder-slot-focus' : '',
                ].join(' ')}
                data-slot-key={slot.key}
                draggable={!readOnly && !slot.is_seed_locked}
                onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', slot.key);
                }}
                onDragOver={(event) => {
                    if (!readOnly && !slot.is_seed_locked) {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                    }
                }}
                onDrop={(event) => {
                    event.preventDefault();
                    swapSlots(event.dataTransfer.getData('text/plain'), slot.key);
                }}
            >
                <div className="bracket-builder-slot-content">
                    <Text type="secondary" className="bracket-builder-position-label">
                        Position {slot.display_position}
                    </Text>
                    {readOnly ? (
                        <Text strong>{participantName(participant)}</Text>
                    ) : (
                        <Select
                            value={slot.participant_id}
                            options={options}
                            onChange={(participantID) =>
                                chooseParticipant(slot.key, participantID)
                            }
                            disabled={slot.is_seed_locked}
                            allowClear
                            placeholder="Unassigned"
                            showSearch
                            filterOption={(input, option) => {
                                const candidate = participantByID.get(
                                    String(option?.value || ''),
                                );
                                return candidate
                                    ? formatBracketParticipantLabel(candidate)
                                          .toLowerCase()
                                          .includes(input.trim().toLowerCase())
                                    : false;
                            }}
                            className="bracket-builder-participant-select"
                        />
                    )}
                </div>
                {draft?.status === 'draft' && !draft.competition_locked && (
                    <Button
                        type={slot.is_seed_locked ? 'primary' : 'text'}
                        icon={
                            slot.is_seed_locked ? <LockOutlined /> : <UnlockOutlined />
                        }
                        disabled={!slot.participant_id}
                        onClick={() => toggleLock(slot.key)}
                        aria-label={
                            slot.is_seed_locked ? 'Unlock seed position' : 'Lock seed position'
                        }
                    />
                )}
            </div>
        );
    };

    const renderRounds = () => {
        const rounds = Array.from(
            workingSlots.reduce((result, slot) => {
                const items = result.get(slot.round) || [];
                items.push(slot);
                result.set(slot.round, items);
                return result;
            }, new Map<number, BracketDraftSlot[]>()),
        ).sort(([left], [right]) => left - right);

        return rounds.map(([round, roundSlots]) => {
            const roundName = bracketRoundName(draft?.round_names, round);
            const matches = Array.from(
                roundSlots.reduce((result, slot) => {
                    const items = result.get(slot.match_index) || [];
                    items.push(slot);
                    result.set(slot.match_index, items);
                    return result;
                }, new Map<number, BracketDraftSlot[]>()),
            ).sort(([left], [right]) => left - right);

            return (
                <section className="bracket-builder-phase" key={`round-${round}`}>
                    <Title level={4}>{roundName}</Title>
                    <Row gutter={[16, 16]}>
                        {matches.map(([matchIndex, matchSlots]) => {
                            const scheduleLabel = draftMatchScheduleLabel(
                                matchSlots,
                                roundSlots,
                                category,
                            );
                            const isFocusedMatch = matchSlots.some((slot) => slot.key === focusSlotKey);
                            const boutNumber = matchSlots.find((slot) => slot.bout_number)?.bout_number;
                            return (
                            <Col xs={24} lg={12} xxl={8} key={`${round}-${matchIndex}`}>
                                <Card
                                    size="small"
                                    title={bracketSessionLabel(roundName, matchIndex + 1, boutNumber)}
                                    extra={
                                        scheduleLabel ? (
                                            <Text type="secondary" className="bracket-builder-match-schedule">
                                                {scheduleLabel}
                                            </Text>
                                        ) : null
                                    }
                                    className={[
                                        'bracket-builder-match',
                                        isFocusedMatch ? 'bracket-builder-match-focus' : '',
                                    ].join(' ')}
                                >
                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                        {matchSlots
                                            .sort((left, right) => left.side - right.side)
                                            .map((slot) => (
                                                <React.Fragment key={slot.key}>
                                                    {renderSlot(slot)}
                                                </React.Fragment>
                                            ))}
                                    </Space>
                                </Card>
                            </Col>
                            );
                        })}
                    </Row>
                </section>
            );
        });
    };

    if (loading) {
        return (
            <div className="bracket-builder-loading">
                {contextHolder}
                <Spin size="large" />
            </div>
        );
    }

    if (loadError || !draft) {
        return (
            <>
                {contextHolder}
                <Alert
                    type="error"
                    showIcon
                    message="Unable to load bracket builder"
                    description={loadError || 'Bracket draft was not found'}
                    action={
                        <Button onClick={() => navigate(`/activity/${activity.id}/category`)}>
                            Back to categories
                        </Button>
                    }
                />
            </>
        );
    }

    const setupReady = Boolean(category?.is_setup_done) && !category?.need_reload;
    const editingDisabled = draft.status !== 'draft' || draft.competition_locked || !setupReady;

    return (
        <Spin spinning={submitting}>
            {contextHolder}
            <div className="bracket-builder">
                <div className="bracket-builder-heading">
                    <div>
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            className="bracket-builder-back"
                            onClick={returnToCategories}
                        >
                            {draft.status === 'published' ? 'Bracket' : 'Categories'}
                        </Button>
                        <Title level={3}>
                            {category?.name || `Category ${cid}`} bracket
                        </Title>
                        <Space wrap>
                            <Tag color={statusColors[draft.status]}>
                                {statusLabels[draft.status]}
                            </Tag>
                            <Tag color="blue">
                                {adjustmentModeLabels[draft.adjustment_mode]}
                            </Tag>
                            <Text type="secondary">
                                {draft.participants.length} participants
                            </Text>
                            {draft.status !== 'not_created' && (
                                <Text type="secondary">
                                    {lockedCount} locked position(s)
                                </Text>
                            )}
                        </Space>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportDraftWorkbook}
                            loading={exporting}
                            disabled={dirty}
                        >
                            Export XLSX
                        </Button>
                        {draft.status !== 'published' && setupReady && (
                            <Upload
                                accept=".xlsx"
                                maxCount={1}
                                showUploadList={false}
                                beforeUpload={beforeImportDraftWorkbook}
                                disabled={dirty || draft.competition_locked || importPreviewLoading || !setupReady}
                            >
                                <Button
                                    icon={<UploadOutlined />}
                                    loading={importPreviewLoading}
                                    disabled={dirty || draft.competition_locked || !setupReady}
                                >
                                    Import XLSX
                                </Button>
                            </Upload>
                        )}
                        {draft.status === 'draft' && setupReady && (
                            <>
                                <Button
                                    icon={<SaveOutlined />}
                                    onClick={saveDraft}
                                    disabled={!dirty || draft.competition_locked || !setupReady}
                                >
                                    Save draft
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={publishDraft}
                                    disabled={dirty || draft.competition_locked || !setupReady}
                                >
                                    Publish
                                </Button>
                            </>
                        )}
                        {draft.status === 'published' && (
                            <>
                                <Button
                                    danger
                                    onClick={resetToDraft}
                                    disabled={draft.competition_locked}
                                >
                                    Reset to draft
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        navigate(
                                            `/activity/${activity.id}/bracket?category=${cid}`,
                                        )
                                    }
                                >
                                    View bracket
                                </Button>
                            </>
                        )}
                    </Space>
                </div>

                {draft.competition_locked && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Bracket positions are locked"
                        description="At least one match in this category has started or finished. The draw can no longer be changed."
                    />
                )}

                {category?.need_reload && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Participant information has changed"
                        description="Reload this category before creating or editing its bracket."
                    />
                )}

                {!category?.is_setup_done && !category?.need_reload && (
                    <Card className="bracket-builder-settings">
                        <div className="bracket-builder-create">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical" size={4} style={{ maxWidth: 520 }}>
                                        <Text>Category setup is not complete</Text>
                                        <Text type="secondary">
                                            Finish Category Setup first. The bracket draft is created after rounds and parallel courts are confirmed.
                                        </Text>
                                    </Space>
                                }
                            >
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        navigate(`/activity/${activity.id}/category/${cid}`)
                                    }
                                >
                                    Go to Category Setup
                                </Button>
                            </Empty>
                        </div>
                    </Card>
                )}

                {draft.status === 'not_created' && setupReady && (
                    <Card className="bracket-builder-settings">
                        <div className="bracket-builder-create">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical" size={4} style={{ maxWidth: 520 }}>
                                        <Text>
                                            Create a draft to randomly place all participants. You can then adjust and lock individual positions.
                                        </Text>
                                        <Text type="secondary">
                                            If needed, set the match schedule in Schedule first, assign players in Bracket view, then come back here to confirm and publish.
                                        </Text>
                                    </Space>
                                }
                            >
                                <Button
                                    type="primary"
                                    onClick={createDraft}
                                >
                                    Create draft
                                </Button>
                            </Empty>
                        </div>
                    </Card>
                )}

                {draft.status !== 'not_created' && setupReady && (
                    <>
                        {draft.status === 'draft' && !editingDisabled && (
                            <Card size="small" className="bracket-builder-toolbar">
                                <Space wrap>
                                    <Button
                                        size="small"
                                        icon={<ReloadOutlined />}
                                        onClick={() => setRandomizeModalOpen(true)}
                                    >
                                        Randomize
                                    </Button>
                                    <Button
                                        size="small"
                                        disabled={lockedCount === 0}
                                        onClick={() =>
                                            Modal.confirm({
                                                title: 'Unlock all positions?',
                                                content:
                                                    'All seed locks will be removed. Participant positions stay unchanged.',
                                                okText: 'Unlock all',
                                                onOk: () =>
                                                    updateLocks(
                                                        new Set(
                                                            participantSlots.map(
                                                                (slot) => slot.key,
                                                            ),
                                                        ),
                                                        false,
                                                    ),
                                            })
                                        }
                                    >
                                        Unlock all
                                    </Button>
                                    <Button
                                        size="small"
                                        icon={<ClearOutlined />}
                                        disabled={assignedCount === 0}
                                        onClick={() =>
                                            Modal.confirm({
                                                title: 'Clear all positions?',
                                                content:
                                                    'All participant positions and seed locks will be cleared. You can then assign and lock the positions you want to keep, save the draft, and randomize the rest later.',
                                                okText: 'Clear all',
                                                okButtonProps: { danger: true },
                                                onOk: clearAllPositions,
                                            })
                                        }
                                    >
                                        Clear all
                                    </Button>
                                    <Text type="secondary">
                                        Drag or select a participant to swap unlocked
                                        positions.
                                    </Text>
                                </Space>
                            </Card>
                        )}
                        {dirty && (
                            <Alert
                                type="info"
                                showIcon
                                banner
                                message="Unsaved changes. Save the draft before randomizing or publishing."
                            />
                        )}
                        {renderRounds()}
                    </>
                )}
            </div>
            <Modal
                title="Import bracket draft XLSX"
                open={Boolean(importPreview)}
                onCancel={() => setImportPreview(null)}
                width={720}
                maskClosable={false}
                footer={
                    importPreview && importPreview.result.row_errors.length === 0 ? (
                        <Space>
                            <Button onClick={() => setImportPreview(null)}>Cancel</Button>
                            <Button
                                type="primary"
                                loading={importConfirming}
                                onClick={confirmImportDraftWorkbook}
                            >
                                Confirm import
                            </Button>
                        </Space>
                    ) : (
                        <Button onClick={() => setImportPreview(null)}>Close</Button>
                    )
                }
            >
                {importPreview &&
                    (importPreview.result.row_errors.length > 0 ? (
                        <>
                            <Alert
                                type="error"
                                showIcon
                                message={`The workbook has ${importPreview.result.row_errors.length} invalid row(s)`}
                                description="Fix the file and pick it again. Nothing was imported."
                                style={{ marginBottom: 16 }}
                            />
                            <Table
                                columns={importRowErrorColumns}
                                dataSource={importPreview.result.row_errors.map(
                                    (error, index) => ({ ...error, key: index }),
                                )}
                                rowKey="key"
                                pagination={false}
                                size="small"
                                scroll={{ y: 320 }}
                            />
                        </>
                    ) : (
                        <>
                            <Paragraph>
                                {importPreview.result.assignments.length} participant
                                position(s) will be applied and saved, replacing the current
                                positions and seed locks.
                            </Paragraph>
                            <Table
                                columns={importPreviewColumns}
                                dataSource={importPreview.result.assignments.map(
                                    (assignment, index) => ({ ...assignment, key: index }),
                                )}
                                rowKey="key"
                                pagination={false}
                                size="small"
                                scroll={{ y: 320 }}
                            />
                        </>
                    ))}
            </Modal>
            <Modal
                title="Randomize positions"
                open={randomizeModalOpen}
                onCancel={() => setRandomizeModalOpen(false)}
                footer={null}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {dirty && (
                        <Alert
                            type="info"
                            showIcon
                            message="You have unsaved changes. They will be saved first, then the randomization runs on the result."
                        />
                    )}
                    <div>
                        <Button block onClick={() => regenerate('unassigned')}>
                            Unassigned positions only
                        </Button>
                        <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                            Every assigned position stays unchanged (locked or not); only
                            empty positions are filled randomly.
                        </Paragraph>
                    </div>
                    <div>
                        <Button block onClick={() => regenerate('unlocked')}>
                            All unlocked positions
                        </Button>
                        <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                            {lockedCount} locked position(s) stay unchanged; every other
                            position is randomly placed.
                        </Paragraph>
                    </div>
                </Space>
            </Modal>
        </Spin>
    );
};

export default BracketBuilder;
